import { memo } from "react";
import { C } from "@/lib/colors";

/**
 * Brand mark — square border with a cyan triangle inscribed inside.
 *
 * Mirrors the landing-page brand mark (added as part of the 2026-05-23
 * landing+dashboard brand sync). The geometry is a 1.5 px square in
 * the page-bright text colour with a right-triangle fill (bottom-left
 * → top-right → bottom-right corner) inset by 3 px. The triangle's
 * accent colour is the cyan brand slot — same role as on the landing,
 * so the mark reads identically across the marketing pages, the
 * sidebar header, and the login screen.
 *
 * Replaces the earlier "F-on-rounded-square" mark. Cohesion with the
 * landing mattered more than glyph specificity — a trader who lands
 * on /, signs in, and opens the dashboard should see the same mark
 * three times rather than two unrelated logos either side of an auth
 * boundary.
 *
 * Defaults:
 *   * size       — 22 px (matches the landing nav and dashboard sidebar
 *                  header on collapsed AND expanded modes). Override
 *                  for OG banners, footer, etc.
 *   * accent     — ``C.cy`` cyan, the brand-chrome slot reserved for
 *                  "we're live / focused / aware" cues. Overridable so
 *                  a future single-tone variant (e.g. monochrome on a
 *                  print export) can drop the cyan without re-authoring
 *                  the geometry.
 *   * borderColor — defaults to ``currentColor`` so the mark inherits
 *                  the parent text colour. The sidebar header uses
 *                  ``text1`` (page-bright); the OG banner uses pure
 *                  white. ``currentColor`` keeps the mark themable
 *                  without prop drilling.
 */
export const Logo = memo(function Logo({
  size = 22,
  accent = C.cy,
  borderColor = "currentColor",
  strokeWidth = 1.5,
}: {
  size?: number;
  accent?: string;
  borderColor?: string;
  strokeWidth?: number;
}) {
  // ViewBox stays at 22 regardless of rendered size so coordinates
  // below are pixel-true for the default. Browsers interpolate cleanly
  // up/down — tested at 14 (collapsed nav) and 64 (OG banner).
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Finder"
      role="img"
    >
      {/* Outer border — 1.5 px square. ``stroke-position: inside``
          isn't standard, so we centre the stroke on the path and let
          the parent's overflow clip the half-pixel that lands outside
          the viewBox edge. Visually indistinguishable at integer
          sizes. */}
      <rect
        x={strokeWidth / 2}
        y={strokeWidth / 2}
        width={22 - strokeWidth}
        height={22 - strokeWidth}
        stroke={borderColor}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Inscribed right triangle — bottom-left → top-right →
          bottom-right. Inset 3 px from the border so the triangle
          doesn't kiss the stroke. Filled with the cyan brand accent. */}
      <polygon
        points="3,19 19,3 19,19"
        fill={accent}
      />
    </svg>
  );
});
